import { createSelector } from '@reduxjs/toolkit'

import { errorView, queryView, type QueryResultLike } from '@krp/client-runtime'

import type {
  OneCHookAgent,
  OneCHookAgentFailure,
  OneCHookAgentView,
  OneCWebhookAttentionRetry,
  OneCWebhookDelivery,
  OneCWebhookError,
  OneCWebhookSecret,
  OneCWebhookState,
  OneCWebhookVm,
} from './types'

/** Заголовок состояния доставки: значок и рамка добавляются компонентом, цвет один не несёт ничего. */
export const WEBHOOK_STATE_TITLES: Readonly<Record<OneCWebhookState, string>> = {
  'no-secret': 'Секрет не выпущен',
  'never-received': 'Пакетов от 1С ещё не было',
  stale: 'Давно нет пакетов',
  receiving: 'Пакеты приходят',
}

/** Четыре места, где ломается доставка со стороны 1С, — в том порядке, в котором их проверяют. */
export const WEBHOOK_CHECKLIST: readonly { name: string; detail: string }[] = [
  { name: 'адрес', detail: 'в константе «Адрес вебхука» — тот же, что показан выше' },
  { name: 'секрет', detail: 'в константе «Секрет вебхука» — последний выпущенный, без пробелов по краям' },
  { name: 'задание «Отправка вебхуков»', detail: 'включено и выполняется по расписанию' },
  { name: 'очередь изменений', detail: 'в ней нет застрявших записей с ошибкой' },
]

/** Что значит отказ службы — обычными словами; код и статус остаются рядом для администратора. */
const REJECTION_WORDS: Readonly<Record<number, string>> = {
  400: 'запрос не разобран',
  409: 'пакет не принят из-за конфликта',
  413: 'пакет больше допустимого размера',
  503: 'очередь уведомлений переполнена, 1С повторит позже',
}

const dateTime = new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium', timeStyle: 'short' })

function whenText(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : dateTime.format(date)
}

/** «5 мин назад», «3 ч назад», «2 сут назад». */
export function agoText(iso: string, now: number): string {
  const at = new Date(iso).getTime()
  if (Number.isNaN(at)) return ''
  const minutes = Math.max(0, Math.floor((now - at) / 60_000))
  if (minutes < 1) return 'только что'
  if (minutes < 60) return `${minutes} мин назад`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours} ч назад`
  return `${Math.floor(hours / 24)} сут назад`
}

/** «1 пакет», «2 пакета», «5 пакетов». */
export function packetsText(count: number): string {
  const tens = count % 100
  const ones = count % 10
  const word = tens >= 11 && tens <= 14 ? 'пакетов' : ones === 1 ? 'пакет' : ones >= 2 && ones <= 4 ? 'пакета' : 'пакетов'
  return `${count} ${word}`
}

/** Очередь внимания брокера: число известно и больше нуля — «требует внимания»; неизвестно — так и сказано. */
export function attentionText(count: number | null): string | null {
  if (count === null) return 'Брокер сообщений не ответил — сколько пакетов требует внимания, неизвестно'
  if (count === 0) return null
  return `Требует внимания: ${packetsText(count)} — служба не приняла их за все попытки`
}

/** Сколько прошло с момента: «меньше минуты», «12 мин», «3 ч», «2 сут». */
export function durationText(iso: string, now: number): string {
  const at = new Date(iso).getTime()
  if (Number.isNaN(at)) return ''
  const minutes = Math.max(0, Math.floor((now - at) / 60_000))
  if (minutes < 1) return 'меньше минуты'
  if (minutes < 60) return `${minutes} мин`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours} ч`
  return `${Math.floor(hours / 24)} сут`
}

function failureText(failure: OneCHookAgentFailure): string {
  return `${failure.status !== null ? `${failure.status} — ` : ''}${failure.message !== '' ? failure.message : 'без ответа сервера'}`
}

/**
 * Строки агента рядом с 1С — только то, о чём стоит сказать (решение 23.10.2026 «связь — только при сбое»): молчит, очередь копится,
 * требует внимания, ошибка настройки. Агент в порядке — `null`, блока нет. Тексты сервера — дословно.
 */
export function agentView(agent: OneCHookAgent, now: number): OneCHookAgentView | null {
  const lines: string[] = []
  if (agent.silent)
    lines.push(
      `Агент молчит ${durationText(agent.lastHeartbeatAt, now)} — последнее сердцебиение ${whenText(agent.lastHeartbeatAt)}` +
        (agent.backlog || agent.needsAttention || agent.pause !== null ? '; ниже — состояние на тот момент' : ''),
    )
  if (agent.pause !== null)
    lines.push(`Ошибка настройки (${durationText(agent.pause.since, now)}): ${failureText(agent.pause)} — пакеты копятся у агента, пока её не исправят`)
  if (agent.backlog)
    lines.push(
      `В очереди агента ${packetsText(agent.queueBatches)}` +
        (agent.oldestQueuedAt !== null ? `, старейший — ${durationText(agent.oldestQueuedAt, now)}` : ''),
    )
  if (agent.attentionBatches > 0)
    lines.push(`Требует внимания: сервер отверг ${packetsText(agent.attentionBatches)}, повтор не поможет — нужно разобрать причину`)
  if (agent.retrying !== null)
    lines.push(`Требует внимания: сервер не принимает пакеты ${durationText(agent.retrying.since, now)} — ${failureText(agent.retrying)}`)
  if (lines.length === 0) return null
  const title = `Агент ${agent.peer} · база ${agent.channel} · версия ${agent.version}`
  return { key: agent.sourceId, title, lines, copyText: [title, ...lines].join('\n') }
}

function buildFacts(delivery: OneCWebhookDelivery, now: number) {
  const lastBatchText =
    delivery.lastBatchAt === null ? 'пакетов не было' : `${whenText(delivery.lastBatchAt)} (${agoText(delivery.lastBatchAt, now)})`
  const dayText = `${delivery.batches24h} пакетов, ${delivery.products24h} товаров`
  const pendingText = delivery.pendingBatches === 0 ? 'нет' : `${delivery.pendingBatches} — ждут перечитывания из 1С`

  // Отказ показываем, только если он новее последнего принятого пакета: старый давно исправлен.
  const rejection = delivery.lastRejection
  const rejectionFresh =
    rejection !== null && (delivery.lastBatchAt === null || new Date(rejection.at).getTime() > new Date(delivery.lastBatchAt).getTime())
  const rejectionText =
    rejection !== null && rejectionFresh
      ? `Последний отказ: ${rejection.status}${REJECTION_WORDS[rejection.status] !== undefined ? ` — ${REJECTION_WORDS[rejection.status]}` : ''}` +
        `${rejection.code !== '' ? ` · код ${rejection.code}` : ''} · ${whenText(rejection.at)}`
      : null

  const failure = delivery.lastFailure
  const failureText =
    failure === null
      ? null
      : `Пакет от ${whenText(failure.receivedAt)} принят, но не перечитан из 1С` +
        `${failure.message !== null && failure.message !== '' ? `: ${failure.message}` : ''} · код ${failure.code} · ` +
        (failure.state === 'blocked' ? 'повтор не поможет, нужно исправить причину' : 'служба повторит сама')

  const warning =
    delivery.state === 'stale'
      ? { title: `Давно нет пакетов — последний был больше ${delivery.staleAfterHours} ч назад` }
      : delivery.state === 'never-received'
        ? { title: 'Секрет выпущен, но пакетов от 1С ещё не приходило' }
        : null
  return { lastBatchText, dayText, pendingText, rejectionText, failureText, warning }
}

export interface OneCWebhookVmInput {
  delivery: QueryResultLike<OneCWebhookDelivery>
  rotate: { isLoading: boolean; isError: boolean; error?: unknown }
  /** Выданный секрет: живёт только в состоянии экрана, показывается один раз. */
  secret: OneCWebhookSecret | null
  /** Текущее время, мс (для «N ч назад»); приходит снаружи, чтобы vm оставался чистой функцией. */
  now: number
  /** Состояние мутации «Повторить» пакеты, требующие внимания. */
  retryAttention: { isLoading: boolean; isError: boolean; error?: unknown; data?: OneCWebhookAttentionRetry | undefined }
}

function errorOf(failed: boolean, error: unknown): OneCWebhookError | null {
  if (!failed || error === undefined) return null
  const view = errorView(error)
  return { message: view.message, code: view.code, correlationId: view.correlationId }
}

/** Фабрика vm экрана «Вебхук 1С»: по экземпляру на смонтированный binding. */
export const makeSelectOneCWebhookVm = () =>
  createSelector(
    [
      (input: OneCWebhookVmInput) => input.delivery.isUninitialized,
      (input: OneCWebhookVmInput) => input.delivery.isLoading,
      (input: OneCWebhookVmInput) => input.delivery.isError,
      (input: OneCWebhookVmInput) => input.delivery.isSuccess,
      (input: OneCWebhookVmInput) => input.delivery.error,
      (input: OneCWebhookVmInput) => input.delivery.data,
      (input: OneCWebhookVmInput) => input.rotate.isLoading,
      (input: OneCWebhookVmInput) => input.rotate.isError,
      (input: OneCWebhookVmInput) => input.rotate.error,
      (input: OneCWebhookVmInput) => input.secret,
      (input: OneCWebhookVmInput) => input.now,
      (input: OneCWebhookVmInput) => input.retryAttention.isLoading,
      (input: OneCWebhookVmInput) => input.retryAttention.isError,
      (input: OneCWebhookVmInput) => input.retryAttention.error,
      (input: OneCWebhookVmInput) => input.retryAttention.data,
    ],
    (
      isUninitialized,
      isLoading,
      isError,
      isSuccess,
      error,
      data,
      rotating,
      rotateFailed,
      rotateError,
      secret,
      now,
      retrying,
      retryFailed,
      retryError,
      retried,
    ) => {
      const query = queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        () => false,
      )
      const delivery = query.status === 'ready' ? (data ?? null) : null
      const facts = delivery === null ? null : buildFacts(delivery, now)
      const rotateFailure = errorOf(rotateFailed, rotateError)
      const vm: OneCWebhookVm = {
        query,
        delivery,
        stateTitle: delivery === null ? '' : WEBHOOK_STATE_TITLES[delivery.state],
        lastBatchText: facts?.lastBatchText ?? '',
        dayText: facts?.dayText ?? '',
        pendingText: facts?.pendingText ?? '',
        warning: facts?.warning ?? null,
        rejectionText: facts?.rejectionText ?? null,
        failureText: facts?.failureText ?? null,
        secret,
        rotate: { busy: rotating, error: rotateFailure },
        attention: {
          text: delivery === null ? null : attentionText(delivery.attentionCount),
          canRetry: delivery !== null && delivery.attentionCount !== null && delivery.attentionCount > 0,
          busy: retrying,
          error: errorOf(retryFailed, retryError),
          done: retried === undefined ? null : `Возвращено в очередь: ${packetsText(retried.requeued)}`,
        },
        agents:
          delivery === null
            ? []
            : delivery.agents.map((agent) => agentView(agent, now)).filter((view): view is OneCHookAgentView => view !== null),
      }
      return vm
    },
  )
